import { TaskGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, Signal, computed, signal } from "@preact/signals";
import { store } from "~/core/store";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createSubscribe } from "../utils";
import { makeAssignment } from "./assignment";
import {
    Task,
    TaskCreateRequest,
    TaskDeleteRequest,
    TaskUpdateRequest,
    TaskUser,
} from "./interfaces";

// task user subscription motivation
// once you're assigned you'll get a notification

export const makeTask = ({ connection, company }: ApiSliceParams) => {
    const tasks = signal<Task[]>([]);
    const taskUsers = signal<TaskUser[]>([]);
    const projectTasks: Record<string, ReadonlySignal<Task[]>> = {};
    const loadingProjects: Record<string, Signal<boolean>> = {};
    const loadedProjects: Record<string, Signal<boolean>> = {};
    const loading = signal(false);
    const loaded = signal(false);
    const assignment = makeAssignment({ connection, company });

    const loadTaskUser = async (
        id: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(id) ? id : [id];
        const { employee } = store.api;
        const items = con.task.user.get({
            id: finalId,
        });
        const result: TaskUser[] = [];

        for await (const user of items) {
            const finalTaskUser = {
                ...user,
                _assignee: user.assignee,
                assignee: await employee.getOrLoad(user.assignee),
                _id: user.id,
                id: user.id.toString(),
            };

            result.push(finalTaskUser);
        }

        taskUsers.value = upsert(taskUsers.peek().slice(), result, "id");

        return result;
    };

    const open = async (task: Task) => {
        const con = connection.value;

        if (!con) {
            return;
        }

        task.loading.value = true;

        const list = con.task.user.list({
            task: task._id,
        });
        const promises: Promise<unknown>[] = [];

        for await (const id of list) {
            promises.push(loadTaskUser(id));
        }

        await Promise.all(promises);

        task.loading.value = false;
    };

    const view = async (task: TaskGet) => {
        const { feeCode, employee, project } = store.api;

        const finalTask = {
            ...task,
            id: task.id.toString(),
            _id: task.id,
            activity: await feeCode.getOrLoad(task.activity),
            reporter: task.reporter
                ? await employee.getOrLoad(Ulid.fromLoHi(task.reporter))
                : null,
            requestedAssignee: task.requested_assignee
                ? await employee.getOrLoad(
                      Ulid.fromLoHi(task.requested_assignee),
                  )
                : null,
            _project: task.project,
            project: await project.getOrLoad(task.project, task.company),
            users: computed(() =>
                taskUsers.value.filter((tu) => tu.task.equals(task.id)),
            ),
            loading: signal(false),
            itag: signal(0),
        };

        open(finalTask);

        return finalTask;
    };

    // @TODO: think about moving to utils
    const getProjectLoading = (id: string) => {
        if (!(id in loadingProjects)) {
            loadingProjects[id] = signal(false);
        }

        return loadingProjects[id];
    };

    const getProjectLoaded = (id: string) => {
        if (!(id in loadedProjects)) {
            loadedProjects[id] = signal(false);
        }

        return loadedProjects[id];
    };

    const loadTask = async (
        taskId: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(taskId) ? taskId : [taskId];
        const list = con.task.get({ id: finalId });
        const promises: Promise<Task>[] = [];

        for await (const task of list) {
            promises.push(view(task));
        }

        const result = await Promise.all(promises);

        // @TODO: sort question
        tasks.value = upsert(tasks.peek().slice(), result, "id").sort((a, b) =>
            a.id.localeCompare(b.id),
        );

        return result;
    };

    const loadProjectTasks = async (id: string) => {
        const con = connection.value;
        const comp = company.value;
        const isLoading = getProjectLoading(id);
        const isLoaded = getProjectLoaded(id);

        if (
            !con ||
            !comp ||
            isLoading.value ||
            isLoaded.value ||
            loaded.value
        ) {
            return;
        }

        isLoading.value = true;

        const list = con.task.list({
            company: comp,
            project: Ulid.fromString(id),
        });
        const promises: Promise<unknown>[] = [];

        for await (const id of list) {
            promises.push(loadTask(id));
        }

        await Promise.all(promises);

        isLoaded.value = true;
        isLoading.value = false;
    };

    const create = async (request: TaskCreateRequest) => {
        const con = connection.value;

        return con.task.create({
            activity: request.activity,
            name: request.name,
            description: request.description,
            company: company.value,
            project: request.project,
            reporter: request.reporter,
            budgeted_start: request.budgetedStart,
            budgeted_end: request.budgetedEnd,
            estimate: request.estimate,
            requested_assignee: request.requestedAssignee,
        });
    };

    const update = async (request: TaskUpdateRequest) => {
        const con = connection.value;

        return con.task.update({
            etag: request.etag,
            id: request.id,
            activity: request.activity,
            name: request.name,
            description: request.description,
            reporter: request.reporter,
            budgeted_start: request.budgetedStart,
            budgeted_end: request.budgetedEnd,
            estimate: request.estimate,
            resource_plan: request.resourcePlan,
            requested_assignee: request.requestedAssignee,
        });
    };

    const remove = async (request: TaskDeleteRequest) => {
        const con = connection.value;

        return con.task.delete({
            etag: request.etag,
            id: request.id,
        });
    };

    const whenProjectLoaded = async (id: string) => {
        const isLoaded = getProjectLoaded(id);
        const isLoading = getProjectLoading(id);

        // it's already loaded
        //
        if (isLoaded.value) {
            return;
        }

        // loading hasn't started yet
        //
        if (!isLoading.value) {
            return loadProjectTasks(id);
        }

        // loading is in progress
        //
        return new Promise<void>((resolve) => {
            const unsubscribe = isLoading.subscribe((value) => {
                if (value) {
                    return;
                }

                resolve();
                unsubscribe();
            });
        });
    };

    const loadAll = async () => {
        const con = connection.value;
        const comp = company.value;

        if (!con || !comp || loading.value || loaded.value) {
            return;
        }

        loading.value = true;

        const list = con.task.list({
            company: comp,
        });
        const promises: Promise<unknown>[] = [];

        for await (const id of list) {
            promises.push(loadTask(id));
        }

        await Promise.all(promises);

        loaded.value = true;
        loading.value = false;
    };

    const cloneProject = async (source: string, target: string) => {
        if (source === target) {
            return;
        }

        await whenProjectLoaded(source);

        const projectTasks = tasks
            .peek()
            .filter((t) => t.project?._id.toString() === source);
        const promises: Promise<unknown>[] = [];

        for (const task of projectTasks) {
            const activity = task.activity?._id;
            const reporter = task.reporter?._id;

            if (!activity || !reporter) {
                continue;
            }

            promises.push(
                create({
                    activity,
                    budgetedEnd: task.budgeted_end,
                    budgetedStart: task.budgeted_start,
                    description: task.description,
                    estimate: task.estimate,
                    name: task.name,
                    project: Ulid.fromString(target),
                    reporter,
                    requestedAssignee: task.requested_assignee,
                }),
            );
        }

        await Promise.all(promises);
    };

    const updateStatus = async (
        _: Task, // @TODO: consider removing
        taskUser: TaskUser,
        status: Readonly<Ulid>,
    ) => {
        const con = connection.value;
        return con.task.user.update({
            etag: taskUser.etag,
            id: taskUser._id,
            status,
        });
    };

    const getTask = async (id: Readonly<Ulid>) => {
        const task = tasks.value.find((t) => t._id.equals(id));

        if (task) {
            return task;
        }

        const newTask = await loadTask(id);

        if (newTask.length) {
            return newTask[0];
        }

        throw new Error("Task not found");
    };

    const byProject = (id: string) => {
        if (!(id in projectTasks)) {
            projectTasks[id] = computed(() =>
                tasks.value.filter((t) => t.project?._id.toString() === id),
            );
            loadProjectTasks(id);
        }

        return projectTasks[id];
    };

    const subscribe = createSubscribe(
        tasks,
        connection,
        (con) => con.task.subscribe(),
        loadTask,
    );

    const subscribeTaskUser = createSubscribe(
        taskUsers,
        connection,
        (con) => con.task.user.subscribe(),
        loadTaskUser,
    );

    return {
        tasks: byProject,
        loading: getProjectLoading,

        create,
        update,
        remove,

        all: tasks,
        allLoading: loading,
        allLoaded: loaded,

        loadAll,
        cloneProject,
        assignment,
        taskUsers,
        loadTaskUser,
        getTask,
        updateStatus,
        subscribe,
        subscribeTaskUser,
    };
};
